import { TenantId } from '../value-objects/TenantId';
import { Email } from '../value-objects/Email';

/**
 * Interfaz para reconstruir Tenant desde persistencia
 */
export interface TenantPersistence {
  id: number | string; // string = GUID desde coofit_core
  nombre: string;
  emailAdministrador: string;
  baseDeDatos: string;
  connectionString: string;
  timeZoneId: string;
  fechaActivoUTC: Date | string;
  active: boolean;
  fechaVencimiento?: Date | string;
  fechaInactivoUTC?: Date | string;
  planAdminId?: number;
}

export class Tenant {
  private constructor(
    private readonly _id: TenantId,
    private readonly _nombre: string,
    private readonly _active: boolean,
    private readonly _emailAdministrador: Email,
    private readonly _baseDeDatos: string,
    private readonly _connectionString: string,
    private readonly _timeZoneId: string,
    private readonly _fechaActivoUTC: Date,
    private readonly _fechaVencimiento?: Date,
    private readonly _fechaInactivoUTC?: Date,
    private readonly _planAdminId?: number
  ) {}

  /**
   * Crea un nuevo Tenant (nueva entidad)
   * id: number | string (GUID desde coofit_core)
   */
  static create(
    id: number | string,
    nombre: string,
    emailAdministrador: string,
    baseDeDatos: string,
    connectionString: string,
    timeZoneId: string,
    fechaActivoUTC: Date,
    active: boolean = true,
    fechaVencimiento?: Date,
    fechaInactivoUTC?: Date,
    planAdminId?: number
  ): Tenant {
    // Validaciones
    if (!nombre || nombre.trim().length === 0) {
      throw new Error('Tenant name cannot be empty');
    }

    return new Tenant(
      TenantId.create(id),
      nombre.trim(),
      active,
      Email.create(emailAdministrador),
      baseDeDatos,
      connectionString,
      timeZoneId,
      fechaActivoUTC,
      fechaVencimiento,
      fechaInactivoUTC,
      planAdminId
    );
  }

  /**
   * Reconstruye un Tenant desde persistencia (sin validaciones)
   * Se usa cuando se carga desde BD o API
   */
  static reconstitute(data: TenantPersistence): Tenant {
    return new Tenant(
      TenantId.fromValue(data.id),
      data.nombre,
      data.active,
      Email.fromValue(data.emailAdministrador),
      data.baseDeDatos,
      data.connectionString,
      data.timeZoneId,
      typeof data.fechaActivoUTC === 'string' ? new Date(data.fechaActivoUTC) : data.fechaActivoUTC,
      data.fechaVencimiento 
        ? (typeof data.fechaVencimiento === 'string' ? new Date(data.fechaVencimiento) : data.fechaVencimiento)
        : undefined,
      data.fechaInactivoUTC
        ? (typeof data.fechaInactivoUTC === 'string' ? new Date(data.fechaInactivoUTC) : data.fechaInactivoUTC)
        : undefined,
      data.planAdminId
    );
  }

  // Getters
  get id(): TenantId {
    return this._id;
  }

  get nombre(): string {
    return this._nombre;
  }

  get emailAdministrador(): Email {
    return this._emailAdministrador;
  }

  get baseDeDatos(): string {
    return this._baseDeDatos;
  }

  get connectionString(): string {
    return this._connectionString;
  }

  get timeZoneId(): string {
    return this._timeZoneId;
  }

  get fechaActivoUTC(): Date {
    return this._fechaActivoUTC;
  }

  get fechaVencimiento(): Date | undefined {
    return this._fechaVencimiento;
  }

  get fechaInactivoUTC(): Date | undefined {
    return this._fechaInactivoUTC;
  }

  get planAdminId(): number | undefined {
    return this._planAdminId;
  }

  isActive(): boolean {
    return this._active;
  }

  /**
   * Activa el tenant
   * Retorna un nuevo Tenant (inmutabilidad)
   */
  activate(): Tenant {
    return new Tenant(
      this._id,
      this._nombre,
      true,
      this._emailAdministrador,
      this._baseDeDatos,
      this._connectionString,
      this._timeZoneId,
      new Date(),
      this._fechaVencimiento,
      undefined,
      this._planAdminId
    );
  }

  /**
   * Desactiva el tenant
   * Retorna un nuevo Tenant (inmutabilidad)
   */
  deactivate(): Tenant {
    return new Tenant(
      this._id,
      this._nombre,
      false,
      this._emailAdministrador,
      this._baseDeDatos,
      this._connectionString,
      this._timeZoneId,
      this._fechaActivoUTC,
      this._fechaVencimiento,
      new Date(),
      this._planAdminId
    );
  }

  /**
   * Cambia el nombre del tenant
   * Retorna un nuevo Tenant (inmutabilidad)
   */
  changeName(newName: string): Tenant {
    if (!newName || newName.trim().length === 0) {
      throw new Error('Tenant name cannot be empty');
    }

    return new Tenant(
      this._id,
      newName.trim(),
      this._active,
      this._emailAdministrador,
      this._baseDeDatos,
      this._connectionString,
      this._timeZoneId,
      this._fechaActivoUTC,
      this._fechaVencimiento,
      this._fechaInactivoUTC,
      this._planAdminId
    );
  }

  /**
   * Actualiza la fecha de vencimiento
   * Retorna un nuevo Tenant (inmutabilidad)
   */
  updateExpirationDate(fechaVencimiento: Date): Tenant {
    return new Tenant(
      this._id,
      this._nombre,
      this._active,
      this._emailAdministrador,
      this._baseDeDatos,
      this._connectionString,
      this._timeZoneId,
      this._fechaActivoUTC,
      fechaVencimiento,
      this._fechaInactivoUTC,
      this._planAdminId
    );
  }
}
